import type { ExportRow, QuestionWiseReport, QuestionWiseRow } from '@ferty9/types';
import type { AdminTab } from './types';

export const tabTitles: Record<string, string> = {
  dashboard: 'Dashboard Overview',
  'clinical-dashboard': 'Clinical Dashboard',
  'business-dashboard': 'Business Dashboard',
  'doctor-report': 'Doctor Report',
  'daily-clinical-report': 'Daily Report',
  'doctor-kpi-report': 'Doctor KPI Report',
  'doctor-ivf-performance': 'Doctor IVF Performance',
  'fcdr-report': 'FCDR Report',
  'marketing-report': 'Marketing Report',
  'btl-report': 'BTL Report',
  'business-report-administration': 'Business Reports Administration',
  'business-data-integrity': 'Business Reports Data Integrity & Retention',
  'business-report-centers': 'Business Report Centre Master',
  'business-report-financial-calendar': 'Business Reports Financial Calendar',
  'business-report-sources': 'Business Report Data Sources',
  'business-revenue-sources': 'Business Revenue Source Master',
  'business-manual-revenue': 'Business Manual Revenue Entries',
  'business-monthly-targets': 'Business Monthly Targets',
  'business-file-imports': 'Business Reports CSV / Excel Imports',
  'business-revenue-category-rules': 'Business Reports Revenue Category Rules',
  'business-revenue-source-definitions': 'Business Reports Source Onboarding Governance',
  'business-reconciliation': 'Business Reports Reconciliation Dashboard',
  'business-operational-monitoring': 'Business Reports Operational Monitoring',
  'feedback-tracker': 'Feedback Tracker',
  'offer-campaigns': 'Offer Campaigns',
  feedback: 'Feedback responses',
  'question-wise': 'Question Wise Response',
  summary: 'Monthly Summary',
  'detailed-summary': 'Detailed Monthly Summary',
  staff: 'Staff Management',
  users: 'User Accounts',
  questions: 'Survey Questions',
  roles: 'Role Management',
  'general-settings': 'General Settings',
  settings: 'Integration Settings',
};

export function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function numberValue(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function formatDate(dateStr: unknown) {
  if (!dateStr) return '-';
  const date = new Date(String(dateStr));
  if (Number.isNaN(date.getTime())) return '-';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

export function toExportRows(rows: object[]): ExportRow[] {
  return rows.map(row => {
    const output: Record<string, string | number | boolean | null> = {};
    Object.entries(row as Record<string, unknown>).forEach(([key, value]) => {
      if (value == null) output[key] = null;
      else if (['string', 'number', 'boolean'].includes(typeof value)) output[key] = value as string | number | boolean;
      else output[key] = JSON.stringify(value);
    });
    return output;
  });
}

export function cleanVerifiedMobileSuffix(value: unknown) {
  return String(value || '-').replace(/\s*\|\s*Verified Mobile:\s*\d+/i, '');
}

export function feedbackExportRows(rows: object[], questions: Array<{ id?: number | string; question_text?: string }>): ExportRow[] {
  const qMap = new Map(questions.map(question => [`dq_${question.id}`, question.question_text || `dq_${question.id}`]));
  const skipQIds = new Set([
    'dq_4a7d343f-b87a-4cde-9edd-19876d65baab',
    'dq_0d4cc20e-53ae-4294-9480-4745b7a84607',
    'dq_b1274bae-59a3-43fe-aeac-58ff88153b84',
    'dq_f4919904-89cb-4ae7-9c60-bc7c792710ef',
    'dq_0284dd2e-856a-448b-9808-668a62c3c45f',
    'dq_ef067549-a7d4-41bf-930e-4ae1c2b004f2',
    'selected_mobile',
    'dq_selected_mobile',
    'language',
    'dq_language',
  ]);

  return rows.map(source => {
    const row = source as Record<string, unknown>;
    const output: Record<string, string | number | boolean | null> = {
      Date: formatDate(row.created_at),
      'Appointment ID': String(row.appointment_id || '-'),
      MRN: String(row.mrn || '-'),
      'Patient Name': String(row.patient_name || '-'),
      Journey: String(row.journey || '-'),
      'Feedback Center': String(row.center || row.center_name || '-'),
      'Verified Number': String(row.verified_mobile || '-'),
      'NPS Score': typeof row.nps_score === 'number' ? row.nps_score : Number(row.nps_score || 0),
      'NPS Category': String(row.nps_bucket || '-'),
      'Overall Feedback': cleanVerifiedMobileSuffix(row.comments || row.nps_feedback || '-'),
      'Let us know what you like the most about us.': String(row.appreciation || '-'),
      'Kindly mention the suggestions that you would like to share with us.': String(row.suggestions || '-'),
    };

    let responses: Record<string, unknown> = {};
    if (typeof row.question_responses === 'string') {
      try {
        responses = JSON.parse(row.question_responses) as Record<string, unknown>;
      } catch {
        responses = {};
      }
    } else if (row.question_responses && typeof row.question_responses === 'object') {
      responses = row.question_responses as Record<string, unknown>;
    }

    Object.entries(responses).forEach(([key, value]) => {
      if (skipQIds.has(key)) return;
      const header = qMap.get(key) || key;
      if (value == null) output[header] = null;
      else if (['string', 'number', 'boolean'].includes(typeof value)) output[header] = value as string | number | boolean;
      else output[header] = JSON.stringify(value);
    });

    output['Staff Member'] = String(row.feedback_by || '-');
    return output;
  });
}

export function questionWiseRows(report: QuestionWiseReport | null): QuestionWiseRow[] {
  const record = report as QuestionWiseReport | null;
  if (!record) return [];
  if (Array.isArray(record.questionData)) return record.questionData;
  if (Array.isArray(record.responses)) return record.responses;
  return [];
}

export function summaryMetric(value: unknown) {
  if (!value || typeof value !== 'object') return '-';
  const record = value as Record<string, unknown>;
  const parts = [
    `Reg: ${record.registrations ?? record.totalRegistrations ?? 0}`,
    `Fb: ${record.feedback_count ?? record.feedbacks ?? 0}`,
    `NPS: ${record.nps ?? record.npsScore ?? '-'}`,
  ];
  return parts.join(' | ');
}
